import { InjectedIntl } from "react-intl";
import { DEFAULT_GLOBAL_DURATIONS, RefType } from "src/constants/constants";
import { appInstanceIconUrlForPlaylist } from "src/helpers/appHelper";
import { generateImgixThumbnail } from "src/helpers/mediaHelper";
import { ItemLike } from "src/utils/publishedDraftDiff";
import { ChannelCompareSide } from "./channelCompareTypes";

/**
 * Entity resolution and effective durations for Channel Compare Changes
 * (GRWBKK-2039). Durations follow the channel listing (ChannelDetail
 * `itemsByZoneContent` + ChannelContentItem), which differs from a playlist:
 * every file falls back to the image default, a fixed app duration wins over a
 * custom one, and the global defaults back up the channel's own.
 */

export interface ChannelListItem extends ItemLike {
  content: {
    _ref?: { id: string; type: string };
    props?: { duration?: number };
  };
}

export interface ChannelEntity {
  title: string;
  subtitle: string;
  thumbnailUrl?: string;
  isAppIcon?: boolean;
  isPlaylist?: boolean;
  thumbnailColor?: string;
  /** Video/audio intrinsic duration — wins over custom/default (files only). */
  intrinsicDurationMs?: number;
  /** appInstanceDurationInSeconds in ms — wins over custom/default. */
  fixedAppDurationMs?: number;
  /** Files only. */
  mimetype?: string;
}

export interface ChannelEntityOptions {
  intl: InjectedIntl;
  secureMediaPolicy?: string;
}

export type DefaultDurations = Record<string, number>;

const SUBTITLE_MESSAGES: Record<
  string,
  { id: string; defaultMessage: string }
> = {
  [RefType.APP]: {
    id: "compare_changes.subtitle.app",
    defaultMessage: "App",
  },
  [RefType.FILE]: {
    id: "compare_changes.subtitle.media",
    defaultMessage: "Media",
  },
  [RefType.LINK]: {
    id: "compare_changes.subtitle.link",
    defaultMessage: "Link",
  },
  [RefType.SITE]: {
    id: "compare_changes.subtitle.site",
    defaultMessage: "Site",
  },
  [RefType.PLAYLIST]: {
    id: "compare_changes.subtitle.playlist",
    defaultMessage: "Playlist",
  },
};

/**
 * Indexes every resolvable entity by id. Later sides overwrite earlier ones,
 * so pass published first and draft second: edited entities display their
 * draft values, while removed items still resolve from the published side.
 */
export const buildChannelEntityIndex = (
  sides: ChannelCompareSide[],
  options: ChannelEntityOptions,
): Map<string, ChannelEntity> => {
  const { intl, secureMediaPolicy } = options;
  const index = new Map<string, ChannelEntity>();
  const subtitle = (refType: string) =>
    intl.formatMessage(SUBTITLE_MESSAGES[refType]);

  for (const side of sides) {
    for (const app of side.appInstancesByChannelId?.nodes ?? []) {
      const fixedSeconds = app.config?.appInstanceDurationInSeconds;
      index.set(app.id, {
        title: app.name ?? "",
        subtitle: subtitle(RefType.APP),
        thumbnailUrl: appInstanceIconUrlForPlaylist(app) || undefined,
        isAppIcon: true,
        fixedAppDurationMs: fixedSeconds ? fixedSeconds * 1000 : undefined,
      });
    }
    for (const file of side.filesByChannelId?.nodes ?? []) {
      const mediaFileOutput = file.fileOutputsByFileId.nodes.find(
        (output) =>
          output?.mimetype?.startsWith("video") ||
          output?.mimetype?.startsWith("audio"),
      );
      index.set(file.id, {
        title: file.name,
        subtitle: subtitle(RefType.FILE),
        thumbnailUrl:
          generateImgixThumbnail(file, secureMediaPolicy, true) || undefined,
        intrinsicDurationMs: mediaFileOutput?.metadata?.duration ?? undefined,
        mimetype: file.mimetype ?? undefined,
      });
    }
    for (const link of side.linksByChannelId?.nodes ?? []) {
      index.set(link.id, {
        title: link.name,
        subtitle: subtitle(RefType.LINK),
        thumbnailUrl: link.fileByFileId
          ? generateImgixThumbnail(
              link.fileByFileId,
              secureMediaPolicy,
              true,
            ) || undefined
          : undefined,
      });
    }
    for (const site of side.sitesByChannelId?.nodes ?? []) {
      index.set(site.id, {
        title: site.name,
        subtitle: subtitle(RefType.SITE),
        thumbnailUrl: site.fileByThumbnailId
          ? generateImgixThumbnail(
              site.fileByThumbnailId,
              secureMediaPolicy,
              true,
            ) || undefined
          : undefined,
      });
    }
    // Playlists stay flat: name and color only, no inner items or thumbnails.
    for (const playlist of side.playlistsByChannelId?.nodes ?? []) {
      index.set(playlist.id, {
        title: playlist.name,
        subtitle: subtitle(RefType.PLAYLIST),
        isPlaylist: true,
        thumbnailColor: playlist.color || undefined,
      });
    }
  }
  return index;
};

/**
 * The duration the channel plays an item for. Playlists play all of their
 * items, so they have none (0).
 */
export const getChannelEffectiveDurationMs = (
  item: ChannelListItem,
  entity: ChannelEntity | undefined,
  defaultDurations: DefaultDurations,
): number => {
  const refType = item.content._ref?.type;
  if (!refType || refType === RefType.PLAYLIST) {
    return 0;
  }
  if (refType === RefType.APP && entity?.fixedAppDurationMs) {
    return entity.fixedAppDurationMs;
  }
  if (refType === RefType.FILE && entity?.intrinsicDurationMs) {
    return entity.intrinsicDurationMs;
  }
  const customDuration = item.content.props?.duration;
  if (customDuration) {
    return customDuration;
  }
  const defaultKey = refType === RefType.FILE ? "image" : refType;
  return (
    defaultDurations[defaultKey] || DEFAULT_GLOBAL_DURATIONS[defaultKey] || 0
  );
};
